// St. George’s Respiratory Questionnaire – grouped per section (paper flow)
// Uses: "radio", "boolean-matrix", "textarea"
// Text is provided via i18n keys defined in public/locales/<locale>/SGRQ.json.

export const SGRQ_GROUPS = {
  PREFACE: "sgrq-preface", // “Please tick one box to show how you describe your current health”
  PART1: "sgrq-part1", // Past 3 months (Q1–Q8)
  PART2_S1: "sgrq-part2-s1",
  PART2_S2: "sgrq-part2-s2",
  PART2_S3: "sgrq-part2-s3",
  PART2_S4: "sgrq-part2-s4",
  PART2_S5: "sgrq-part2-s5",
  PART2_S6: "sgrq-part2-s6",
  PART2_S7: "sgrq-part2-s7",
};

// If your screen renders by group, use this to drive order/section dividers.
export const SGRQ_GROUP_ORDER = [
  SGRQ_GROUPS.PREFACE,
  SGRQ_GROUPS.PART1,
  SGRQ_GROUPS.PART2_S1,
  SGRQ_GROUPS.PART2_S2,
  SGRQ_GROUPS.PART2_S3,
  SGRQ_GROUPS.PART2_S4,
  SGRQ_GROUPS.PART2_S5,
  SGRQ_GROUPS.PART2_S6,
  SGRQ_GROUPS.PART2_S7,
];

const radio = (options) => ({ fieldType: "radio", options });
const opt = (value, labelKey) => ({ value, labelKey });

const yesNo = [
  opt(true, "sgrq.scales.yesNo.true"),
  opt(false, "sgrq.scales.yesNo.false"),
];

const freq5 = [
  opt("mostDays", "sgrq.scales.freq5.mostDays"),
  opt("severalDays", "sgrq.scales.freq5.severalDays"),
  opt("fewDays", "sgrq.scales.freq5.fewDays"),
  opt("withInfections", "sgrq.scales.freq5.withInfections"),
  opt("notAtAll", "sgrq.scales.freq5.notAtAll"),
];

const attacks5 = [
  opt("gt3", "sgrq.scales.attacks5.gt3"),
  opt("eq3", "sgrq.scales.attacks5.eq3"),
  opt("eq2", "sgrq.scales.attacks5.eq2"),
  opt("eq1", "sgrq.scales.attacks5.eq1"),
  opt("none", "sgrq.scales.attacks5.none"),
];

const duration4 = [
  opt("weekOrMore", "sgrq.scales.duration4.weekOrMore"),
  opt("threeOrMoreDays", "sgrq.scales.duration4.threeOrMoreDays"),
  opt("oneOrTwoDays", "sgrq.scales.duration4.oneOrTwoDays"),
  opt("lessThanDay", "sgrq.scales.duration4.lessThanDay"),
];

const goodDays5 = [
  opt("none", "sgrq.scales.goodDays5.none"),
  opt("oneTwo", "sgrq.scales.goodDays5.oneTwo"),
  opt("threeFour", "sgrq.scales.goodDays5.threeFour"),
  opt("nearlyEvery", "sgrq.scales.goodDays5.nearlyEvery"),
  opt("every", "sgrq.scales.goodDays5.every"),
];

const importance4 = [
  opt("mostImportant", "sgrq.scales.importance4.mostImportant"),
  opt("quiteALot", "sgrq.scales.importance4.quiteALot"),
  opt("fewProblems", "sgrq.scales.importance4.fewProblems"),
  opt("noProblem", "sgrq.scales.importance4.noProblem"),
];

const work3 = [
  opt("stopWork", "sgrq.scales.work3.stopWork"),
  opt("interferes", "sgrq.scales.work3.interferes"),
  opt("noEffect", "sgrq.scales.work3.noEffect"),
];

const overallLimit4 = [
  opt("noStop", "sgrq.scales.overallLimit4.noStop"),
  opt("oneTwo", "sgrq.scales.overallLimit4.oneTwo"),
  opt("mostThings", "sgrq.scales.overallLimit4.mostThings"),
  opt("everything", "sgrq.scales.overallLimit4.everything"),
];

const booleanMatrixTrueFalse = (rowsKeyBase, rowIds) => ({
  fieldType: "boolean-matrix",
  columns: [
    { value: true, labelKey: "sgrq.scales.trueFalse.true" },
    { value: false, labelKey: "sgrq.scales.trueFalse.false" },
  ],
  rows: rowIds.map((rid) => ({
    id: rid,
    labelKey: `${rowsKeyBase}.${rid}`,
  })),
});

const sgrqQuestionnaire = [
  // --- Preface: FIRST, before anything else ---
  {
    id: "SGRQ_CURRENT_HEALTH",
    section: "part1",
    group: SGRQ_GROUPS.PREFACE,
    titleKey: "sgrq.preface.currentHealth.title",
    required: true,
    ...radio([
      opt("veryGood", "sgrq.preface.currentHealth.options.veryGood"),
      opt("good", "sgrq.preface.currentHealth.options.good"),
      opt("fair", "sgrq.preface.currentHealth.options.fair"),
      opt("poor", "sgrq.preface.currentHealth.options.poor"),
      opt("veryPoor", "sgrq.preface.currentHealth.options.veryPoor"),
    ]),
  },

  // --- PART 1 (past 3 months) ---
  {
    id: "SGRQ_P1_Q1",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q1.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(freq5),
  },
  {
    id: "SGRQ_P1_Q2",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q2.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(freq5),
  },
  {
    id: "SGRQ_P1_Q3",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q3.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(freq5),
  },
  {
    id: "SGRQ_P1_Q4",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q4.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(freq5),
  },
  {
    id: "SGRQ_P1_Q5",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q5.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(attacks5),
  },
  {
    id: "SGRQ_P1_Q6",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q6.title",
    required: false,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(duration4),
  },
  {
    id: "SGRQ_P1_Q7",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q7.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(goodDays5),
  },
  {
    id: "SGRQ_P1_Q8",
    section: "part1",
    group: SGRQ_GROUPS.PART1,
    titleKey: "sgrq.part1.q8.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHint",

    ...radio(yesNo),
  },

  // --- PART 2: Section 1 ---
  {
    id: "SGRQ_P2_S1_CONDITION",
    section: "part2.s1",
    group: SGRQ_GROUPS.PART2_S1,
    titleKey: "sgrq.part2.s1.condition.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",

    ...radio(importance4),
  },
  {
    id: "SGRQ_P2_S1_WORK",
    section: "part2.s1",
    group: SGRQ_GROUPS.PART2_S1,
    titleKey: "sgrq.part2.s1.work.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",

    ...radio(work3),
  },

  // --- PART 2: Section 2 (True/False matrix) ---
  {
    id: "SGRQ_P2_S2_MATRIX",
    section: "part2.s2",
    group: SGRQ_GROUPS.PART2_S2,
    titleKey: "sgrq.part2.s2.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",

    ...booleanMatrixTrueFalse("sgrq.part2.s2.rows", [
      "r1",
      "r2",
      "r3",
      "r4",
      "r5",
      "r6",
      "r7",
    ]),
  },

  // --- PART 2: Section 3 ---
  {
    id: "SGRQ_P2_S3_MATRIX",
    section: "part2.s3",
    group: SGRQ_GROUPS.PART2_S3,
    titleKey: "sgrq.part2.s3.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",
    ...booleanMatrixTrueFalse("sgrq.part2.s3.rows", [
      "r1",
      "r2",
      "r3",
      "r4",
      "r5",
      "r6",
    ]),
  },

  // --- PART 2: Section 4 ---
  {
    id: "SGRQ_P2_S4_MATRIX",
    section: "part2.s4",
    group: SGRQ_GROUPS.PART2_S4,
    titleKey: "sgrq.part2.s4.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",

    ...booleanMatrixTrueFalse("sgrq.part2.s4.rows", [
      "r1",
      "r2",
      "r3",
      "r4",
      "r5",
      "r6",
      "r7",
      "r8",
    ]),
  },

  // --- PART 2: Section 5 (skip if no medication) ---
  {
    id: "SGRQ_P2_S5_MATRIX",
    section: "part2.s5",
    group: SGRQ_GROUPS.PART2_S5,
    titleKey: "sgrq.part2.s5.title",
    required: false,
    helperTextKey: "sgrq.options.singleChoiceHintTheseDays",
    // (Optional) If you have a medication boolean elsewhere, add visibleIf: { dependsOn: 'HAS_MEDS', equals: true }
    ...booleanMatrixTrueFalse("sgrq.part2.s5.rows", ["r1", "r2", "r3", "r4"]),
  },

  // --- PART 2: Section 6 ---
  {
    id: "SGRQ_P2_S6_MATRIX",
    section: "part2.s6",
    group: SGRQ_GROUPS.PART2_S6,
    titleKey: "sgrq.part2.s6.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintBreathing",
    ...booleanMatrixTrueFalse("sgrq.part2.s6.rows", [
      "r1",
      "r2",
      "r3",
      "r4",
      "r5",
      "r6",
      "r7",
      "r8",
      "r9",
    ]),
  },

  // --- PART 2: Section 7 (matrix + textarea + overall radio) ---
  {
    id: "SGRQ_P2_S7_MATRIX",
    section: "part2.s7",
    group: SGRQ_GROUPS.PART2_S7,
    titleKey: "sgrq.part2.s7.title",
    required: true,
    helperTextKey: "sgrq.options.singleChoiceHintChest",

    ...booleanMatrixTrueFalse("sgrq.part2.s7.rows", [
      "r1",
      "r2",
      "r3",
      "r4",
      "r5",
    ]),
  },

  {
    id: "SGRQ_P2_S7_OTHER",
    section: "part2.s7",
    group: SGRQ_GROUPS.PART2_S7,
    titleKey: "sgrq.part2.s7.otherActivities.prompt",
    descriptionKey: "sgrq.part2.s7.otherActivities.description",
    placeholderKey: "sgrq.part2.s7.otherActivities.placeholder",
    fieldType: "textarea",
    required: false,
  },

  {
    id: "SGRQ_P2_S7_OVERALL",
    section: "part2.s7",
    group: SGRQ_GROUPS.PART2_S7,
    titleKey: "sgrq.part2.s7.overall.title",
    required: true,
    ...radio(overallLimit4),
  },
];

export default sgrqQuestionnaire;
